import { AirlineLogo } from '../ui/AirlineLogo';
import { RouteDisplay } from '../ui/RouteDisplay';
import { Button } from '@perch/ui';
import { X } from 'lucide-react';
import { getAirlineName } from '../../lib/constants';
import type { PendingLookup } from '../../lib/types';

interface PendingBookingCardProps {
  pending: PendingLookup;
  onConfirm: (pending: PendingLookup) => void;
  onAddManually: (pending: PendingLookup) => void;
  onDismiss: (id: string) => void;
}

/**
 * Dashboard card for pending PNR lookups and low-confidence email parses.
 * Three visual states:
 *
 *   looking_up         → stone pulsing dot + shimmer placeholder
 *   found/awaiting_confirmation → route + flight + "Confirm Details" CTA
 *   failed/timeout     → "Couldn't look up" + "Add Details Manually" CTA
 *
 * Reads from the pending_lookups table via Realtime subscription (in parent).
 */
export function PendingBookingCard({ pending, onConfirm, onAddManually, onDismiss }: PendingBookingCardProps) {
  const isLoading = pending.status === 'looking_up';
  const isReady = pending.status === 'found' || pending.status === 'awaiting_confirmation';
  const isFailed = pending.status === 'failed' || pending.status === 'timeout';

  const result = pending.lookup_result;
  const firstSegment = result?.segments?.[0];
  const lastSegment = result?.segments?.[result.segments.length - 1];

  return (
    <div
      className="bg-white dark:bg-stone-900 rounded-xl p-4 border border-stone-200/60 dark:border-stone-700/50"
      role="status"
      aria-live="polite"
    >
      {/* Header: airline logo + PNR */}
      <div className="flex items-center gap-3 mb-3">
        <AirlineLogo code={pending.airline_code} size={40} />
        <div className="flex-1 min-w-0">
          <p className="font-mono font-semibold text-stone-900 dark:text-stone-100 tracking-tight">
            {pending.pnr}
          </p>
          <p className="text-xs text-stone-400 dark:text-stone-500">
            {getAirlineName(pending.airline_code)}
          </p>
        </div>
        <button
          onClick={() => onDismiss(pending.id)}
          className="p-1 text-stone-300 dark:text-stone-600 hover:text-stone-500 dark:hover:text-stone-400 transition-colors"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Looking up state */}
      {isLoading && (
        <div className="animate-in fade-in duration-300">
          <div className="flex items-center gap-2 mb-2">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-stone-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-stone-500" />
            </span>
            <span className="text-sm text-stone-600 dark:text-stone-400">
              Looking Up Your Booking...
            </span>
          </div>
          <p className="text-xs text-stone-400 dark:text-stone-500 ml-4">
            Usually takes a few minutes.
          </p>
          {/* Shimmer placeholder for route */}
          <div className="mt-3 flex items-center gap-3">
            <div className="animate-pulse h-5 w-12 bg-stone-200 dark:bg-stone-700 rounded" />
            <span className="text-stone-300 dark:text-stone-600">→</span>
            <div className="animate-pulse h-5 w-12 bg-stone-200 dark:bg-stone-700 rounded" />
          </div>
        </div>
      )}

      {/* Ready to confirm state */}
      {isReady && firstSegment && lastSegment && (
        <div className="animate-in fade-in duration-300">
          <div className="mb-3">
            <RouteDisplay
              segments={[{ origin: firstSegment.origin, destination: lastSegment.destination }]}
              size="md"
            />
            <p className="text-xs text-stone-500 dark:text-stone-400 mt-1">
              {getAirlineName(firstSegment.carrier_code)} {firstSegment.flight_number}
              {firstSegment.departure_datetime && (
                <> · {new Date(firstSegment.departure_datetime).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</>
              )}
            </p>
          </div>

          <Button onClick={() => onConfirm(pending)} fullWidth>
            Confirm Details →
          </Button>
        </div>
      )}

      {/* Failed state */}
      {isFailed && (
        <div className="animate-in fade-in duration-300">
          <p className="text-sm text-stone-600 dark:text-stone-400 mb-3">
            Couldn't look up this booking.
          </p>
          <Button onClick={() => onAddManually(pending)} fullWidth>
            Add Details Manually →
          </Button>
        </div>
      )}
    </div>
  );
}
